// @fileoverview : 경남-품질관리 > 작업표준서관리
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import {
  ItemStandardAttachmentsGet200Response,
  ItemStandardAttachmentsGet200ResponseRowsInner,
  MasterApiItemStandardAttachmentsItemStandardAttachmentIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconDownload, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

import {
  mutateWorkStandard,
  workStandardFile,
  WORK_STANDARD_KEY,
} from "@/api/workStandard/useWorkStandardQuery";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import ForceDarkModeStyles from "@/utils/darkModeStyle";
import dateTimeUtil from "@/utils/dateTimeUtil";

export const WorkStandardListPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();

  const { mutate: workStandardListDelete } = useMutation({
    mutationFn: (params: MasterApiItemStandardAttachmentsItemStandardAttachmentIdDeleteRequest) =>
      mutateWorkStandard.delete(params).mutationFn(undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORK_STANDARD_KEY] });
      customAlert(
        `${t("message.deleteSuccess", { item: `${t("workStandard.workStandard")}` })}`,
        t("common.deleteSuccess"),
        "green",
      );
    },
    onError: (error: AxiosError<Error>) => {
      customAlert(
        error.response?.data?.message ?? "작업표준서 관리 삭제에 실패하였습니다.",
        "생성 실패",
        "red",
      );
    },
  });

  const columns: readonly Column<ItemStandardAttachmentsGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "itemCode",
      name: t("item.code"),
      sortable: true,
      resizable: true,
      formatter: ({ row }: any) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={row.itemCode} />,
                null,
                t("common.detail", { item: t("item.code") }),
              )
            }
          >
            {row.itemCode ?? "-"}
          </LinkAnchor>
        );
      },
    },
    {
      key: "fileName",
      name: t("common.fileName"),
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: t("common.createdAt"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return <>{dateTimeUtil(row.createdAt ?? "-")}</>;
      },
    },
    {
      key: "updatedAt",
      name: t("common.updatedAt"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return <>{dateTimeUtil(row.updatedAt ?? "-")}</>;
      },
    },
    {
      key: "creatorUserCode",
      name: t("common.creator"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return <>{row.creatorUserCode ?? "-"}</>;
      },
    },
    {
      key: "updaterUserCode",
      name: t("common.updater"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return <>{row.updaterUserCode ?? "-"}</>;
      },
    },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const downloadWorkStandard = () => {
          handleDownload({
            filePath: formatterProps.row.filePath ?? "",
            fileName: formatterProps.row.fileName ?? "",
          });
        };

        const deleteWorkStandard = () => {
          openModal(
            <ConfirmForm
              message={t("message.rowDelete")}
              yesCallback={() => {
                workStandardListDelete({
                  itemStandardAttachmentId: formatterProps.row.id as number,
                });
              }}
              noCallback={() => {
                console.log("삭제 취소");
              }}
              commonCallback={() => {
                closeModal({});
              }}
            />,
            null,
            t("common.delete", { item: t("workStandard.workStandard") }),
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon variant="subtle" color="green">
              <IconDownload onClick={downloadWorkStandard} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red">
              <IconTrash onClick={deleteWorkStandard} />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDownload = async ({ filePath, fileName }: { filePath: string; fileName: string }) => {
    try {
      const response = await workStandardFile.download(filePath);

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("다운로드 실패:", error);
      alert("파일 다운로드 중 문제가 발생했습니다.");
    }
  };

  return (
    <Flex w="100%" h="100%" gap="0.625rem" direction="column">
      <Flex p="0.75rem" direction="column" gap="0.625rem">
        <Text fz="1.25rem" fw={600}>
          {t("menu.quality.work")}
        </Text>
        <Text fz="0.75rem">{t("common.subTitle", { item: t("menu.quality.work") })}</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="0.75rem">
        <Flex wrap="wrap" className={flexGrowable}>
          <Flex gap="0.625rem" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline" radius="sm">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="0.625rem" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex className={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ForceDarkModeStyles />
          <ListTable<
            ItemStandardAttachmentsGet200Response,
            ItemStandardAttachmentsGet200ResponseRowsInner
          >
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<ItemStandardAttachmentsGet200Response> />
      </Flex>
    </Flex>
  );
};
